/**
 * Utility functions for password generation and security.
 * 
 * Provides secure password generation with configurable strength requirements.
 */

/**
 * Generates a strong random password with uppercase, lowercase, numbers, and special characters.
 * 
 * <p>The generated password ensures:
 * <ul>
 *   <li>At least one uppercase letter</li>
 *   <li>At least one lowercase letter</li>
 *   <li>At least one number</li>
 *   <li>At least one special character</li>
 *   <li>Random character distribution to avoid predictable patterns</li>
 * </ul>
 * 
 * @param {number} length - The desired password length (default: 16)
 * @returns {string} A randomly generated strong password
 */
export const generateStrongPassword = (length = 16) => {
  const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lowercase = "abcdefghijklmnopqrstuvwxyz";
  const numbers = "0123456789";
  const special = "!@#$%^&*()_+-=[]{}|;:,.<>?";
  const allChars = uppercase + lowercase + numbers + special;
  
  let password = "";
  
  // Ensure at least one character from each category
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += special[Math.floor(Math.random() * special.length)];
  
  // Fill the rest randomly
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  
  // Shuffle the password to avoid predictable pattern
  return password.split("").sort(() => Math.random() - 0.5).join("");
};

